import React, { useState, useRef, useEffect } from "react";
import { useIntl, FormattedMessage } from "react-intl";
import { useParams, useNavigate } from "react-router-dom";
import { Form, Button } from "react-bootstrap";

import { useUiFeedback } from "../../../context/ui-feedback/ui-feedback";
import {
  createWaiting,
  closeWaiting,
  createErrorFeedback,
  createWarningFeedback,
  createQuestionFeedback,
  createSuccessFeedback,
} from "../../../context/ui-feedback/action";
import { PageLayout } from "../../common/page-layout/page-layout";
import { ControllerRow } from "../../common/controller-row/controller-row";
import { SalesOrderDetails } from "./component/sales-order-detail";
import { TemplateMultiSelect } from "./component/template-multi-select";
import { PrimaryWorkerSelect } from "./component/primary-worker-select";
import { WorkersSelect } from "./component/workers-select";
import { DateSelect } from "./component/date-select";
import {
  useLoadServiceWorksheetById,
  useUpdateServiceWorksheet,
  useUpdateWorkersChange,
  useReloadSalesOrder,
  useCloseServiceWorksheet,
} from "../../../api/service-worksheet-api";
import {
  isSalesOrderUpdateability,
  checkServiceWorksheetUpdateability,
  calculatedStatus,
} from "../../../utils/service-worksheet-utils/service-worksheet-utils";
import { useListWorkersSchedules } from "../../../api/calendar-api";
import { formatDate, getEndOfDay } from "../../../utils/date-utils/date-utils";

export const EditServiceWorksheet = () => {
  const { id } = useParams();
  const intl = useIntl();
  const navigate = useNavigate();
  const loadServiceWorksheetById = useLoadServiceWorksheetById();
  const updateServiceWorksheet = useUpdateServiceWorksheet();
  const updateWorkersChange = useUpdateWorkersChange();
  const reloadSalesOrder = useReloadSalesOrder();
  const listWorkersSchedules = useListWorkersSchedules();
  const closeServiceWorksheet = useCloseServiceWorksheet();

  const { uiFeedbackDispatch } = useUiFeedback();

  const serviceWorksheetFormRef = useRef(null);
  const [editable, setEditable] = useState(false);
  const [formValidated, setFormValidated] = useState(false);
  const localWorksheetId = useRef(null);
  const [reload, setReload] = useState(false);
  const [originalServiceWorksheet, setOriginalServiceWorksheet] =
    useState(null);
  const [serviceWorksheet, setServiceWorksheet] = useState(null);
  const [ediatbleWorkers, setEdiatbleWorkers] = useState(editable);

  const [fielValidStatus, setFieldValidStatus] = useState({
    note: true,
    templates: [],
    primaryWorker: true,
    workers: true,
    date: true,
  });

  useEffect(() => {
    if (serviceWorksheet) {
      const templateNameCounts = {};
      serviceWorksheet.templates.forEach((obj) => {
        let name = obj.name;
        if (templateNameCounts[name]) {
          templateNameCounts[name]++;
        } else {
          templateNameCounts[name] = 1;
        }
      });

      const templatesValidity = [];
      serviceWorksheet.templates.forEach((template, index) => {
        templatesValidity[index] =
          templateNameCounts[template.name] === 1 && template?.template?.id > 0;
      });

      setFieldValidStatus({
        note: true,
        templates: templatesValidity,
        primaryWorker: true,
        workers: true,
        date: isValidDate(serviceWorksheet.date),
      });
    }
  }, [serviceWorksheet]);

  useEffect(() => {
    const loadServiceWorksheet = async () => {
      createWaiting(uiFeedbackDispatch);
      setServiceWorksheet(null);
      try {
        await loadServiceWorksheetById(id, (data) => {
          if (data instanceof Error) {
            throw data;
          } else {
            if (data.date.type === null) {
              data.date.type = "FULL_DAY";
            }
            setServiceWorksheet({
              ...data,
            });
            setOriginalServiceWorksheet({
              ...data,
            });
            setEditable(
              !checkServiceWorksheetUpdateability({ ...data }, { ...data })
                .error,
            );
            setEdiatbleWorkers(
              !checkServiceWorksheetUpdateability({ ...data }, { ...data })
                .error,
            );
          }
        });
      } catch (ex) {
        if (ex.response?.status === 401 || ex.response?.status === 403) {
          navigate("/");
        } else {
          createErrorFeedback(
            uiFeedbackDispatch,
            intl.formatMessage({
              id: "page.editserviceworksheet.exception.load.title",
            }),
            intl.formatMessage({
              id: "page.editserviceworksheet.exception.load.description",
            }),
          );
        }
      } finally {
        closeWaiting(uiFeedbackDispatch);
      }
    };

    const checkNotEmpy = (items) => {
      return items.filter((item) => item === null).length === 0;
    };

    if (
      (reload || localWorksheetId.current !== id) &&
      checkNotEmpy([
        loadServiceWorksheetById,
        intl,
        navigate,
        uiFeedbackDispatch,
      ])
    ) {
      setReload(false);
      localWorksheetId.current = id;
      loadServiceWorksheet();
    }
  }, [
    id,
    reload,
    localWorksheetId,
    loadServiceWorksheetById,
    intl,
    navigate,
    uiFeedbackDispatch,
  ]);

  const buildPaths = () => {
    return [
      {
        link: "/",
        title: intl.formatMessage({ id: "page.welcome.title" }),
      },
      {
        link: "/serviceworksheets",
        title: intl.formatMessage({ id: "page.serviceworksheets.title" }),
      },
    ];
  };

  const onSubmitServiceWorksheet = async (event) => {
    event.preventDefault();
    await saveServiceWorksheet({
      chechWorkersSchedule: true,
    });
  };

  const onSubmitWorkersChange = async (event) => {
    event.preventDefault();
    createQuestionFeedback(
      uiFeedbackDispatch,
      intl.formatMessage({
        id: "page.editserviceworksheet.modal.workersupdate.title",
      }),
      intl.formatMessage({
        id: "page.editserviceworksheet.modal.workersupdate.question",
      }),
      null,
      () => {
        saveWorkersChange({ chechWorkersSchedule: true });
      },
    );
  };

  const onCloseServiceWorksheet = async (event) => {
    event.preventDefault();
    createQuestionFeedback(
      uiFeedbackDispatch,
      intl.formatMessage({
        id: "page.editserviceworksheet.modal.closeserviceworksheet.title",
      }),
      intl.formatMessage({
        id: "page.editserviceworksheet.modal.closeserviceworksheet.question",
      }),
      null,
      async () => {
        try {
          createWaiting(uiFeedbackDispatch);
          await closeServiceWorksheet(serviceWorksheet.id, () => {
            setReload(true);
            createSuccessFeedback(
              uiFeedbackDispatch,
              intl.formatMessage({
                id: "page.editserviceworksheet.success.closeserviceworksheet",
              }),
            );
          });
        } catch (ex) {
          if (ex.response?.status === 401 || ex.response?.status === 403) {
            navigate("/");
          } else {
            createErrorFeedback(
              uiFeedbackDispatch,
              intl.formatMessage({
                id: "page.editserviceworksheet.exception.closeserviceworksheet.title",
              }),
              intl.formatMessage({
                id: "page.editserviceworksheet.exception.closeserviceworksheet.description",
              }),
            );
          }
        } finally {
          closeWaiting(uiFeedbackDispatch);
        }
      },
    );
  };

  const setSchedules = (schedules, handleOk) => {
    createQuestionFeedback(
      uiFeedbackDispatch,
      intl.formatMessage({
        id: "page.editserviceworksheet.modal.schedulesquestion.title",
      }),
      intl.formatMessage({
        id: "page.editserviceworksheet.modal.schedulesquestion.question",
      }),
      <ul className="mt-2">
        {schedules.map((schedule, index) => (
          <li key={index}>
            <b>{schedule.user.name}</b>: {schedule.title}{" "}
            <i>
              ({schedule.period.start} - {schedule.period.end})
            </i>
          </li>
        ))}
      </ul>,
      handleOk,
    );
  };

  const saveServiceWorksheet = async ({ chechWorkersSchedule }) => {
    setFormValidated(true);

    const error = checkServiceWorksheetUpdateability(
      originalServiceWorksheet,
      serviceWorksheet,
    );

    if (error.error) {
      createErrorFeedback(
        uiFeedbackDispatch,
        intl.formatMessage({
          id: `page.editserviceworksheet.exception.${error.key}.title`,
        }),
        intl.formatMessage({
          id: `page.editserviceworksheet.exception.${error.key}.description`,
        }),
      );
    } else {
      const invalidFieldCounts = Object.keys(fielValidStatus)
        .map((key) => {
          if (Array.isArray(fielValidStatus[key])) {
            if (fielValidStatus[key].length === 0) {
              return false;
            }
            return (
              Object.keys(fielValidStatus[key])
                .map((index) => {
                  return fielValidStatus[key][index];
                })
                .filter((value) => !value).length === 0
            );
          } else {
            return fielValidStatus[key];
          }
        })
        .filter((value) => !value).length;

      if (invalidFieldCounts === 0) {
        try {
          createWaiting(uiFeedbackDispatch);
          let schedulesResp = [];
          if (
            chechWorkersSchedule &&
            calculatedStatus(serviceWorksheet) === "ALLOCATED"
          ) {
            const userIds = [
              serviceWorksheet.primaryWorker.id,
              ...serviceWorksheet.workers.map((worker) => worker.id),
            ];
            const start = serviceWorksheet.date.date1Time;
            const end =
              serviceWorksheet.date.type === "FULL_DAY"
                ? getEndOfDay(serviceWorksheet.date.date1Time).getTime()
                : serviceWorksheet.date.date2Time;
            await listWorkersSchedules(userIds, start, end, (data) => {
              if (data instanceof Error) {
                throw data;
              } else {
                schedulesResp = data;
              }
            });
          }
          if (schedulesResp.length === 0) {
            await updateServiceWorksheet(serviceWorksheet, (data) => {
              if (data instanceof Error) {
                throw data;
              } else {
                createSuccessFeedback(
                  uiFeedbackDispatch,
                  intl.formatMessage({
                    id: "page.editserviceworksheet.success.update",
                  }),
                );
                if (data.warningCode) {
                  createWarningFeedback(
                    uiFeedbackDispatch,
                    intl.formatMessage({
                      id: `page.editserviceworksheet.exception.${data.warningCode}.title`,
                    }),
                    intl.formatMessage({
                      id: `page.editserviceworksheet.exception.${data.warningCode}.description`,
                    }),
                    () => {
                      setReload(true);
                    },
                  );
                } else {
                  setReload(true);
                }
              }
            });
          } else {
            setSchedules(schedulesResp, () => {
              saveServiceWorksheet({ chechWorkersSchedule: false });
            });
          }
        } catch (ex) {
          if (ex.response?.status === 401 || ex.response?.status === 403) {
            navigate("/");
          } else {
            createErrorFeedback(
              uiFeedbackDispatch,
              intl.formatMessage({
                id: "page.editserviceworksheet.exception.update.title",
              }),
              intl.formatMessage({
                id: "page.editserviceworksheet.exception.update.description",
              }),
            );
          }
        } finally {
          closeWaiting(uiFeedbackDispatch);
        }
      }
    }
  };

  const saveWorkersChange = async ({ chechWorkersSchedule }) => {
    try {
      createWaiting(uiFeedbackDispatch);

      let schedulesResp = [];
      if (chechWorkersSchedule) {
        const userIds = [
          serviceWorksheet.primaryWorker.id,
          ...serviceWorksheet.workers.map((worker) => worker.id),
        ];
        const start = serviceWorksheet.date.date1Time;
        const end =
          serviceWorksheet.date.type === "FULL_DAY"
            ? getEndOfDay(serviceWorksheet.date.date1Time).getTime()
            : serviceWorksheet.date.date2Time;
        await listWorkersSchedules(userIds, start, end, (data) => {
          if (data instanceof Error) {
            throw data;
          } else {
            schedulesResp = data;
          }
        });
      }
      if (schedulesResp.length === 0) {
        await updateWorkersChange(serviceWorksheet, (data) => {
          if (data instanceof Error) {
            throw data;
          } else {
            createSuccessFeedback(
              uiFeedbackDispatch,
              intl.formatMessage({
                id: "page.editserviceworksheet.success.update",
              }),
            );
            if (data.warningCode) {
              createWarningFeedback(
                uiFeedbackDispatch,
                intl.formatMessage({
                  id: `page.editserviceworksheet.exception.${data.warningCode}.title`,
                }),
                intl.formatMessage({
                  id: `page.editserviceworksheet.exception.${data.warningCode}.description`,
                }),
                () => {
                  setReload(true);
                },
              );
            } else {
              setReload(true);
            }
          }
        });
      } else {
        setSchedules(schedulesResp, () => {
          saveWorkersChange({ chechWorkersSchedule: false });
        });
      }
    } catch (ex) {
      if (ex.response?.status === 401 || ex.response?.status === 403) {
        navigate("/");
      } else {
        createErrorFeedback(
          uiFeedbackDispatch,
          intl.formatMessage({
            id: "page.editserviceworksheet.exception.update.title",
          }),
          intl.formatMessage({
            id: "page.editserviceworksheet.exception.update.description",
          }),
        );
      }
    } finally {
      closeWaiting(uiFeedbackDispatch);
    }
  };

  const isValidDate = (date) => {
    const now = new Date();
    if (date?.date1Time !== null && date.date1Time < now.getTime()) {
      return false;
    } else if (date.type === "PERIOD" && date.date2Time < date.date1Time) {
      return false;
    } else {
      return true;
    }
  };

  const reloadCurrentSalesOrder = async () => {
    try {
      createWaiting(uiFeedbackDispatch);
      await reloadSalesOrder(serviceWorksheet.id, (data) => {
        if (data instanceof Error) {
          throw data;
        } else {
          createSuccessFeedback(
            uiFeedbackDispatch,
            intl.formatMessage({
              id: "page.editserviceworksheet.success.reloadsalesorder",
            }),
          );
          setReload(true);
        }
      });
    } catch (ex) {
      if (ex.response?.status === 401 || ex.response?.status === 403) {
        navigate("/");
      } else {
        createErrorFeedback(
          uiFeedbackDispatch,
          intl.formatMessage({
            id: "page.editserviceworksheet.exception.reloadsalesorder.title",
          }),
          intl.formatMessage({
            id: "page.editserviceworksheet.exception.reloadsalesorder.title",
          }),
        );
      }
    } finally {
      closeWaiting(uiFeedbackDispatch);
    }
  };

  if (!serviceWorksheet) {
    return null;
  }

  return (
    <PageLayout
      paths={buildPaths()}
      title={
        serviceWorksheet.onSiteWorksheet
          ? intl.formatMessage({
              id: "page.serviceworksheets.label.onsiteservice",
            })
          : serviceWorksheet.salesOrder.number
      }
    >
      <>
        {serviceWorksheet.onSiteWorksheet ||
        serviceWorksheet.salesOrder.id ===
          "00000000-0000-0000-0000-000000000000" ? null : (
          <ControllerRow>
            <div className="text-end mb-2">
              <Button
                variant="outline-primary"
                onClick={reloadCurrentSalesOrder}
                disabled={!isSalesOrderUpdateability(serviceWorksheet)}
              >
                <FormattedMessage id="page.editserviceworksheet.button.reloadsalesorder" />
              </Button>
            </div>
          </ControllerRow>
        )}
        <SalesOrderDetails
          swId={serviceWorksheet.id}
          onSiteWorksheet={serviceWorksheet.onSiteWorksheet}
          salesOrder={serviceWorksheet.salesOrder}
          status={serviceWorksheet.status}
          isMobilSync={serviceWorksheet.mobilSync}
          created={serviceWorksheet.createdUser}
          createdDate={serviceWorksheet.createdDate}
          modify={serviceWorksheet.modifyUser}
          modifyDate={serviceWorksheet.modifyDate}
          pdf={serviceWorksheet.pdf}
          shortPdf={serviceWorksheet.shortPdf}
          email={serviceWorksheet.emailStatus}
        />
        <Form
          className="mt-4"
          ref={serviceWorksheetFormRef}
          onSubmit={(event) => {
            if (editable) {
              onSubmitServiceWorksheet(event);
            } else if (serviceWorksheet.status === "ALLOCATED") {
              if (ediatbleWorkers) {
                onSubmitWorkersChange(event);
              } else {
                setEdiatbleWorkers(true);
              }
            }
            if (serviceWorksheet.status === "DONE") {
              onCloseServiceWorksheet(event);
            }
          }}
        >
          {serviceWorksheet.onSiteWorksheet ? null : (
            <Form.Group className="mb-3">
              <Form.Label>
                <FormattedMessage id="data.serviceworksheet.form.note.label" />
              </Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                isValid={formValidated && fielValidStatus.note}
                isInvalid={formValidated && !fielValidStatus.note}
                placeholder={intl.formatMessage({
                  id: "data.serviceworksheet.form.note.placeholder",
                })}
                value={
                  serviceWorksheet?.note === null ? "" : serviceWorksheet?.note
                }
                disabled={!editable}
                onChange={(event) => {
                  setServiceWorksheet({
                    ...serviceWorksheet,
                    note: event.target.value,
                  });
                }}
              />
            </Form.Group>
          )}
          {serviceWorksheet.onSiteWorksheet ? null : (
            <Form.Group className="mb-3">
              <Form.Label>
                <FormattedMessage id="data.serviceworksheet.form.templates.label" />
              </Form.Label>
              <TemplateMultiSelect
                selectedTemplates={serviceWorksheet.templates}
                formValidated={formValidated}
                templatesValidationStatus={fielValidStatus.templates}
                disabled={!editable}
                handleTemplateSelect={(templates) => {
                  setServiceWorksheet({
                    ...serviceWorksheet,
                    templates: templates,
                  });
                }}
              />
            </Form.Group>
          )}
          <Form.Group className="mb-3">
            <Form.Label>
              <FormattedMessage id="data.serviceworksheet.form.primaryworker.label" />
            </Form.Label>
            <PrimaryWorkerSelect
              exludedWorkers={
                serviceWorksheet?.workers ? [...serviceWorksheet?.workers] : []
              }
              isValid={formValidated && fielValidStatus.primaryWorker}
              isInvalid={formValidated && !fielValidStatus.primaryWorker}
              primaryWorker={serviceWorksheet?.primaryWorker}
              disabled={!ediatbleWorkers}
              handlePrimaryWorkerSelect={(user) => {
                setServiceWorksheet({
                  ...serviceWorksheet,
                  primaryWorker: user,
                });
              }}
            />
          </Form.Group>
          {serviceWorksheet.onSiteWorksheet ? null : (
            <Form.Group className="mb-3">
              <Form.Label>
                <FormattedMessage id="data.serviceworksheet.form.workers.label" />
              </Form.Label>
              <WorkersSelect
                exludedWorkers={
                  serviceWorksheet?.primaryWorker
                    ? [serviceWorksheet?.primaryWorker]
                    : []
                }
                workers={serviceWorksheet?.workers}
                isValid={formValidated && fielValidStatus.workers}
                isInvalid={formValidated && !fielValidStatus.workers}
                disabled={!ediatbleWorkers}
                handleWorkersSelect={(users) => {
                  setServiceWorksheet({
                    ...serviceWorksheet,
                    workers: users,
                  });
                }}
              />
            </Form.Group>
          )}
          <Form.Group className="mb-3">
            <Form.Label>
              <FormattedMessage id="data.serviceworksheet.form.date.label" />
            </Form.Label>
            <DateSelect
              date={serviceWorksheet?.date}
              isValid={formValidated && fielValidStatus.date}
              isInvalid={formValidated && !fielValidStatus.date}
              disabled={!editable}
              handleDateSelect={(date) => {
                setServiceWorksheet({
                  ...serviceWorksheet,
                  date: {
                    ...date,
                    date1:
                      date.date1Time === null
                        ? null
                        : formatDate(new Date(date.date1Time)),
                    date2:
                      date.date2Time === null
                        ? null
                        : formatDate(new Date(date.date2Time)),
                  },
                });
              }}
            />
          </Form.Group>
          {editable ? (
            <Button variant="primary" onClick={onSubmitServiceWorksheet}>
              <FormattedMessage id="page.editserviceworksheet.button.save" />
            </Button>
          ) : null}
          {!editable && serviceWorksheet.status === "ALLOCATED" ? (
            ediatbleWorkers ? (
              <Button variant="primary" onClick={onSubmitWorkersChange}>
                <FormattedMessage id="page.editserviceworksheet.button.save" />
              </Button>
            ) : (
              <Button
                variant="primary"
                onClick={() => {
                  setEdiatbleWorkers(true);
                }}
              >
                <FormattedMessage id="page.editserviceworksheet.button.modifyWorker" />
              </Button>
            )
          ) : null}
          {serviceWorksheet.status === "DONE" ? (
            <Button variant="primary" onClick={onCloseServiceWorksheet}>
              <FormattedMessage id="page.editserviceworksheet.button.close" />
            </Button>
          ) : null}
        </Form>
      </>
    </PageLayout>
  );
};
